import React, { useEffect, useMemo, useState, useCallback } from "react";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import HActionButton from "../../../hcomponents/HActionButton";
import HPagination from "../../../hcomponents/HPagination";
import { Badge } from "../../../shadcn/ui/badge";
import { Card, CardContent } from "../../../shadcn/ui/card";
import { MoreVertical, Calendar, MessageSquare, Eye } from "lucide-react";
import { useHistory } from "react-router-dom";
import apiService from "../../../services/apiService";
import * as localService from "../../../services/localService";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";
import moment from "moment";

const FEED_TABS = [
  { label: "All Posts", value: "all" },
  { label: "Drafts", value: "drafts" },
  { label: "Scheduled", value: "scheduled" },
];

// Example shape kept for reference; now using API data
const _FEED_DATA = [
  {
    id: 1,
    type: "Announcement",
    status: "Live",
    title: "Discounted rates on co-working space 30% Off",
    description: "Special offer for community members on premium co-working spaces",
    image: "/assets/img/Web/Spacia/imgplaceholder.png",
    date: "Jul 12",
    comments: 12,
    views: 234,
    badges: ["Announcement", "Live"],
    priority: "HIGH",
    readStatus: false,
    cta: true,
  },
  {
    id: 2,
    type: "Event",
    status: "Scheduled",
    title: "Monthly Community Meetup",
    description: "Join us for networking and collaboration",
    image: "/assets/img/Web/Spacia/imgplaceholder.png",
    date: "Jul 15",
    comments: 5,
    views: 156,
    badges: ["Event", "Scheduled"],
    priority: "LOW",
    readStatus: true,
    cta: false,
  },
  {
    id: 3,
    type: "Announcement",
    status: "Scheduled",
    title: "New Partnership Announcement",
    description: "Exciting new partnership to benefit our community",
    image: "/assets/img/Web/Spacia/imgplaceholder.png",
    date: "Jul 20",
    comments: 8,
    views: 189,
    badges: ["Announcement", "Scheduled"],
    priority: "MEDIUM",
    readStatus: false,
    cta: false,
  },
  {
    id: 4,
    type: "Event",
    status: "Live",
    title: "Summer Workspace Event",
    description: "Join our summer networking event with refreshments and activities",
    image: "/assets/img/Web/Spacia/imgplaceholder.png",
    date: "Jul 25",
    comments: 15,
    views: 298,
    badges: ["Event", "Live"],
    priority: "LOW",
    readStatus: true,
    cta: false,
  },
  {
    id: 5,
    type: "Announcement",
    status: "Draft",
    title: "Office Space Expansion Announcement",
    description: "We are expanding our office space to accommodate more community members",
    image: "/assets/img/Web/Spacia/imgplaceholder.png",
    date: "Jul 30",
    comments: 0,
    views: 0,
    badges: ["Announcement", "Draft"],
    priority: "URGENT",
    readStatus: false,
    cta: true,
  },
  {
    id: 6,
    type: "Event",
    status: "Scheduled",
    title: "Tech Talk Series Launch",
    description: "Monthly tech talks featuring industry experts and thought leaders",
    image: "/assets/img/Web/Spacia/imgplaceholder.png",
    date: "Aug 5",
    comments: 7,
    views: 142,
    badges: ["Event", "Scheduled"],
    priority: "LOW",
    readStatus: true,
    cta: false,
  },
];

const getTabCount = (tab, posts) => {
  if (tab === "all") return posts.length;
  if (tab === "drafts") return posts.filter(post => post.status === "Draft").length;
  if (tab === "scheduled") return posts.filter(post => post.status === "Scheduled").length;
  return 0;
};

const Feed = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [imageErrors, setImageErrors] = useState({});
  const [posts, setPosts] = useState([]);
  const [_loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [_totalElements, setTotalElements] = useState(0);
  const history = useHistory();

  const currentUser = localService.getUser();
  const canCreatePost = FeatureAccess.canCreateAnnouncement(currentUser);

  const displayTabs = useMemo(() => {
    if (!canCreatePost) return [{ label: "All Posts", value: "all" }];
    return FEED_TABS;
  }, [canCreatePost]);

  // Format notification helper
  const formatNotification = useCallback(announcement => {
    const createdAt = announcement.createdAt
      ? moment({
          year: announcement.createdAt.year,
          month: announcement.createdAt.monthValue - 1,
          date: announcement.createdAt.dayOfMonth,
          hour: announcement.createdAt.hour,
          minute: announcement.createdAt.minute,
          second: announcement.createdAt.second,
        })
      : moment();
    const schedule = announcement.datetimeToSend
      ? moment({
          year: announcement.datetimeToSend.year,
          month: announcement.datetimeToSend.monthValue - 1,
          date: announcement.datetimeToSend.dayOfMonth,
          hour: announcement.datetimeToSend.hour,
          minute: announcement.datetimeToSend.minute,
          second: announcement.datetimeToSend.second,
        })
      : null;
    let status = "Live";
    if (!schedule) status = "Draft";
    else if (schedule.isAfter(moment())) status = "Scheduled";
    const type = "Announcement";
    return {
      id: announcement.id,
      type,
      status,
      title: announcement.title,
      description: announcement.body || announcement.message || "",
      image: "/assets/img/Web/Spacia/imgplaceholder.png",
      date: createdAt.format("MMM D"),
      comments: 0,
      views: 0,
      badges: [type, status],
      priority: announcement.priority || "LOW",
      readStatus: announcement.readStatus || false,
      cta: announcement.cta || false,
    };
  }, []);

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        setLoading(true);
        logger.log("Fetching announcements for host feed...");
        const pageSize = 10; // Use smaller page size for pagination
        const response = await apiService.getCreatedNotifications(currentPage, pageSize);
        // Handle different response structures
        let announcements = [];
        let totalCount = 0;

        if (response.data) {
          // Check if response.data is an array directly
          if (Array.isArray(response.data)) {
            announcements = response.data;
            totalCount = response.data.length;
          }
          // Check if response.data has a content property (paged response)
          else if (response.data.content && Array.isArray(response.data.content)) {
            announcements = response.data.content;
            totalCount = response.data.totalElements || response.data.total || announcements.length;
          }
          // Check if response.data has a data property
          else if (response.data.data && Array.isArray(response.data.data)) {
            announcements = response.data.data;
            totalCount = response.data.totalElements || response.data.total || announcements.length;
          }
        }

        // Try to get total count from headers as fallback
        if (totalCount === announcements.length && response.headers && response.headers["x-total-count"]) {
          totalCount = parseInt(response.headers["x-total-count"]);
        }

        if (announcements.length >= 0) {
          logger.log("Parsed announcements:", announcements);

          if (announcements.length > 0) {
            const transformed = announcements.map(formatNotification);
            setPosts(transformed);
            setTotalPages(Math.ceil(totalCount / pageSize));
            setTotalElements(totalCount);
          } else {
            setPosts([]);
            setTotalPages(0);
            setTotalElements(0);
            logger.log("No announcements found");
          }
        } else {
          logger.error("Invalid response format:", response.data);
          showApiMessage(
            {
              response: {
                status: 400,
                data: { message: "Invalid data received from server" },
              },
            },
            "fetch announcements"
          );
        }
      } catch (error) {
        logger.error("Error fetching announcements:", error);
        showApiMessage(error, "fetch announcements");
        setPosts([]);
        setTotalPages(0);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    };
    fetchFeed();
  }, [currentPage, formatNotification]);

  const filteredPosts = useMemo(() => {
    if (activeTab === "all") return posts;
    return posts.filter(post => (activeTab === "drafts" ? post.status === "Draft" : post.status === "Scheduled"));
  }, [activeTab, posts]);

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
  };

  // Reset to first page when tab changes
  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  return (
    <HPageContainer>
      <div className="flex flex-col h-full">
        <HHeader
          rightButton={
            canCreatePost ? (
              <HActionButton
                label="+ Create Post"
                variant="primary"
                size="medium"
                onClick={() => history.push("/host/feed/create")}
              />
            ) : null
          }
        />
        <div className="flex-1 overflow-y-auto p-6 bg-[#f6f8fc]">
          {/* Custom Tab Bar */}
          <div className="mb-6 w-full">
            {displayTabs.length > 1 && (
              <div className="flex w-full border border-[#E2E4EA] rounded-2xl bg-white overflow-hidden">
                {displayTabs.map((tab, idx) => (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`flex-1 text-center transition-all font-medium text-base py-2 ${
                      activeTab === tab.value
                        ? "bg-[#3F3DED] text-white rounded-[24px] shadow-md"
                        : "text-black bg-transparent"
                    }`}
                    style={{
                      marginLeft: idx === 0 ? 16 : 0,
                      marginRight: idx === displayTabs.length - 1 ? 16 : 0,
                    }}
                  >
                    {tab.label} <span className="font-normal">({getTabCount(tab.value, posts)})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Feed Cards Grid */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-16 text-center text-gray-500">
                <div className="flex justify-center items-center mb-3 w-16 h-16 bg-blue-50 rounded-full">
                  <MessageSquare className="w-8 h-8 text-blue-400" />
                </div>
                <div className="text-sm">No posts in this tab</div>
              </div>
            ) : (
              filteredPosts.map(post => (
                <Card key={post.id} className="overflow-hidden relative bg-white shadow-sm">
                  <div className="relative">
                    {post.image && !imageErrors[post.id] ? (
                      <img
                        src={post.image}
                        alt={post.title}
                        className="object-cover w-full h-44"
                        onError={() =>
                          setImageErrors(prev => ({
                            ...prev,
                            [post.id]: true,
                          }))
                        }
                      />
                    ) : (
                      <div className="flex justify-center items-center w-full h-44 bg-blue-50">
                        <MessageSquare className="w-8 h-8 text-blue-400" />
                      </div>
                    )}
                    <button className="absolute top-3 right-3 p-1 bg-white rounded-full shadow">
                      <MoreVertical className="w-5 h-5 text-gray-500" />
                    </button>
                    <div className="flex absolute top-3 left-3 gap-2">
                      {post.badges.map(badge => (
                        <Badge
                          key={badge}
                          variant="secondary"
                          className={`text-xs px-2 py-0.5 ${badge === "Live" ? "bg-green-100 text-green-700" : badge === "Scheduled" ? "bg-orange-100 text-orange-700" : badge === "Draft" ? "bg-gray-200 text-gray-600" : "bg-blue-100 text-blue-700"}`}
                        >
                          {badge}
                        </Badge>
                      ))}
                      {post.priority && post.priority !== "LOW" && (
                        <Badge
                          variant="secondary"
                          className={`text-xs px-2 py-0.5 ${
                            post.priority === "HIGH"
                              ? "bg-red-100 text-red-700"
                              : post.priority === "URGENT"
                                ? "bg-orange-100 text-orange-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {post.priority}
                        </Badge>
                      )}
                      {post.cta && (
                        <Badge variant="outline" className="text-xs bg-white border-gray-300">
                          Action Required
                        </Badge>
                      )}
                      {!post.readStatus && (
                        <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                          New
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="mb-1 text-lg font-semibold text-gray-900">{post.title}</h3>
                    <p className="mb-3 text-sm text-gray-600">{post.description}</p>
                    <div className="flex gap-4 items-center text-xs text-gray-500">
                      <div className="flex gap-1 items-center">
                        <Calendar className="w-3 h-3" />
                        <span>{post.date}</span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <MessageSquare className="w-3 h-3" />
                        <span>{post.comments}</span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <Eye className="w-3 h-3" />
                        <span>{post.views}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Pagination */}
          {posts.length > 0 && (
            <HPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          )}
        </div>
      </div>
    </HPageContainer>
  );
};

export default Feed;
